import dataLayerPush from '../utilities/dataLayerPush';
import debounce from 'lodash/debounce';

// Types
type CarouselElements = {
	nextButton: HTMLButtonElement | null;
	previousButton: HTMLButtonElement | null;
	items: NodeListOf<Element>;
	slides: HTMLElement | null;
	navigation: HTMLElement;
};

type CarouselState = {
	currentVisibleItem: HTMLElement | null;
	isManualScroll: boolean;
	scrollTimeout: number | null;
	pips: HTMLElement[];
};

function logCarousel(message: string, data?: any): void {
	const timestamp = new Date().toISOString();
	const prefix = '[CAROUSEL-DEBUG]';

	if (data !== undefined) {
		console.debug(`${prefix} ${timestamp}: ${message}`, data);
	} else {
		console.debug(`${prefix} ${timestamp}: ${message}`);
	}
}

// Initialize all carousels on the page
function initializeCarousels(): void {
	const carousels = document.querySelectorAll<HTMLElement>('.carousel');
	if (carousels.length < 1) return;
	carousels.forEach(initCarousel);
}

// Export the initialization function for external use
export function initCarousel(carousel: HTMLElement): void {
	setupCarouselNavigation(carousel);
	addCarouselItemAnalytics(carousel.querySelectorAll('.carousel__slides > *'));
}

// Get carousel DOM elements
function getCarouselElements(carousel: HTMLElement): CarouselElements {
	const nextButton = carousel.querySelector<HTMLButtonElement>('[data-carousel-next-button]');
	const previousButton = carousel.querySelector<HTMLButtonElement>('[data-carousel-prev-button]');
	const items = carousel.querySelectorAll('.carousel__item');
	const slides = carousel.querySelector<HTMLElement>('.carousel__slides');

	// Create or get navigation container
	let navigation = carousel.querySelector<HTMLElement>('[data-carousel-navigation]');

	if (!navigation) {
		navigation = document.createElement('div');
		navigation.setAttribute('data-carousel-navigation', '');
		navigation.classList.add('carousel__navigation');
		carousel.appendChild(navigation);
	}

	return { nextButton, previousButton, items, slides, navigation };
}

// Create and setup navigation pips
function createNavigationPips(items: NodeListOf<Element>, navigation: HTMLElement, state: CarouselState): void {
	items.forEach((item, index) => {
		const pip = document.createElement('button');

		pip.type = 'button';
		pip.setAttribute('aria-label', `Go to slide ${index + 1}`);
		pip.classList.add('carousel-nav__pip');

		pip.addEventListener('click', () => {
			state.isManualScroll = false;

			(item as HTMLElement).scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center'
			});

			dataLayerPush({
				event: 'Carousel pip click',
				Slide: index + 1
			});
		});

		navigation.appendChild(pip);
		state.pips.push(pip);
	});
}

const SCROLL_TOLERANCE = 5;

function isScrolledToStart(slides: HTMLElement | null): boolean {
	if (!slides) return true;

	return slides.scrollLeft <= SCROLL_TOLERANCE;
}

function isScrolledToEnd(slides: HTMLElement | null): boolean {
	if (!slides) return true;

	const { scrollLeft, scrollWidth, clientWidth } = slides;
	const maxScroll = scrollWidth - clientWidth;

	return maxScroll <= SCROLL_TOLERANCE || scrollLeft >= maxScroll - SCROLL_TOLERANCE;
}

function areAllSlidesVisible(slides: HTMLElement | null): boolean {
	if (!slides) return false;

	return slides.scrollWidth <= slides.clientWidth + SCROLL_TOLERANCE;
}

// Update navigation buttons visibility and state
function updateButtonVisibility(carousel: HTMLElement, elements: CarouselElements, state: CarouselState): void {
	const { nextButton, previousButton, items, slides } = elements;
	const { currentVisibleItem, pips } = state;
	const allVisible = areAllSlidesVisible(slides);

	logCarousel(`updateButtonVisibility: carousel [${carousel.className}] allVisible [${allVisible}]`, { slides });

	// Toggle container padding class
	carousel.querySelector('.carousel__container')?.classList.toggle('carousel__container--no-padding', allVisible);

	// Update next button
	if (nextButton) {
		nextButton.hidden = allVisible;
		nextButton.disabled = isScrolledToEnd(slides);
	}

	// Update previous button
	if (previousButton) {
		previousButton.hidden = allVisible;
		previousButton.disabled = isScrolledToStart(slides);
	}

	// Update active pip
	if (currentVisibleItem) {
		const currentIndex = Array.from(items).indexOf(currentVisibleItem);
		pips.forEach((pip, index) => {
			pip.classList.toggle('carousel-nav__pip--current', index === currentIndex);
			pip.hidden = allVisible;
		});
	}
}

// Setup intersection observer for tracking visible slides
function setupIntersectionObserver(elements: CarouselElements, state: CarouselState, updateFn: () => void): void {
	const { items, slides } = elements;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					state.currentVisibleItem = entry.target as HTMLElement;
				}
			});

			updateFn();
		},
		{
			root: slides,
			threshold: 0.5,
			rootMargin: '0px'
		}
	);

	items.forEach((item) => observer.observe(item));
}

// Setup the next button functionality
function setupNextButton(nextButton: HTMLButtonElement, state: CarouselState, slides: HTMLElement | null): void {
	nextButton.addEventListener('mousedown', (e) => e.preventDefault());
	nextButton.addEventListener('click', () => {
		if (!slides) return;
		state.isManualScroll = false;

		// Get the width of one slide (assuming all slides have the same width)
		const slideWidth = slides.querySelector<HTMLElement>('.carousel__item')?.offsetWidth ?? 0;

		slides.scrollBy({
			left: slideWidth,
			behavior: 'smooth'
		});

		dataLayerPush({
			event: 'Carousel button click',
			Arrow: 'Next',
			IsManualScroll: 'false'
		});
	});
}

// Setup the previous button functionality
function setupPreviousButton(previousButton: HTMLButtonElement, state: CarouselState, slides: HTMLElement | null): void {
	previousButton.addEventListener('mousedown', (e) => e.preventDefault());
	previousButton.addEventListener('click', () => {
		if (!slides) return;
		state.isManualScroll = false;

		// Get the width of one slide (assuming all slides have the same width)
		const slideWidth = slides.querySelector<HTMLElement>('.carousel__item')?.offsetWidth ?? 0;

		slides.scrollBy({
			left: -slideWidth,
			behavior: 'smooth'
		});

		dataLayerPush({
			event: 'Carousel button click',
			Arrow: 'Previous',
			IsManualScroll: 'false'
		});
	});
}

// Setup manual scroll detection
function setupScrollDetection(slides: HTMLElement | null, state: CarouselState, updateFn: () => void): void {
	const debouncedUpdate = debounce(updateFn, 100);

	slides?.addEventListener('scroll', () => {
		if (state.scrollTimeout) {
			window.clearTimeout(state.scrollTimeout);
		}

		state.isManualScroll = true;
		debouncedUpdate();

		state.scrollTimeout = window.setTimeout(() => {
			state.isManualScroll = false;
		}, 100);
	});
}

// Setup focus handling for carousel items
function setupFocusHandling(items: NodeListOf<Element>): void {
	items.forEach((item) => {
		// Prevent the carousel from scrolling to the next/previous card when clicking on the card.
		// Instead, we let the browser navigate to the link from the anchor tag
		item.querySelectorAll('a').forEach(link => {
			link.addEventListener('mousedown', (e) => {
				e.preventDefault();
			});
		});

		item.addEventListener(
			'focus',
			() => {
				(item as HTMLElement).scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center'
				});
			},
			true
		);
	});
}

// Main function to setup carousel navigation
function setupCarouselNavigation(carousel: HTMLElement): void {
	const elements = getCarouselElements(carousel);
	const { nextButton, previousButton, items, slides, navigation } = elements;

	if (items.length === 0) return;

	// Initialize state
	const state: CarouselState = {
		currentVisibleItem: null,
		isManualScroll: false,
		scrollTimeout: null,
		pips: []
	};

	// Create navigation pips
	if (carousel.classList.contains('carousel--stripe-promotion')) {
		createNavigationPips(items, navigation, state);
	}

	// Create update function with closure over elements
	const updateFn = () => updateButtonVisibility(carousel, elements, state);
	const debouncedUpdate = debounce(updateFn, 250);

	// Setup scroll detection
	setupScrollDetection(slides, state, updateFn);

	// Setup focus handling
	setupFocusHandling(items);

	// Setup intersection observer
	setupIntersectionObserver(elements, state, updateFn);

	// Setup navigation buttons
	if (nextButton) setupNextButton(nextButton, state, slides);
	if (previousButton) setupPreviousButton(previousButton, state, slides);

	// Initial check and resize listener
	updateFn();
	window.addEventListener('resize', debouncedUpdate);
}

// Add analytics attributes to carousel items
function addCarouselItemAnalytics(items: NodeListOf<Element>): void {
	items.forEach((item, index) => {
		item.setAttribute('data-analytics-category', 'carousel item');
		item.setAttribute('data-analytics-order', `${index + 1}/${items.length}`);

		const label = item.querySelector('h2, h3, h4');
		if (label?.textContent) {
			item.setAttribute('data-analytics-label', label.textContent.trim());
		}
	});
}

// Initialize all carousels on load
initializeCarousels();
